import { Expense, Car, CarTask, supabase } from './supabase';

const WORKER_URL = 'https://mycarcosts.gtreulands.workers.dev';

export async function processReceiptFromImage(imageBlob: Blob): Promise<Partial<Expense> | null> {
    try {
        const base64WithHeader = await blobToBase64(imageBlob);
        const base64 = base64WithHeader.split(',')[1];
        
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Unauthorized');

        const response = await fetch(`${WORKER_URL}/ai/receipt`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({ 
                imageBase64: base64,
                mimeType: imageBlob.type 
            })
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({ error: 'Extraction failed' }));
            throw new Error(err.error || 'Extraction failed');
        }

        const result = await response.json();

        return {
            title: result.title || '',
            amount: result.amount || 0,
            date: result.date || new Date().toISOString().split('T')[0],
            fuel_liters: result.fuel_liters || null,
            description: result.description || '',
            type: (result.fuel_liters || result.title?.toLowerCase().includes('fuel') || result.title?.toLowerCase().includes('statol') || result.title?.toLowerCase().includes('neste') || result.title?.toLowerCase().includes('circle')) ? 'fuel' : 'maintenance'
        } as Partial<Expense>;
    } catch (error) {
        console.error('Error in AI processing:', error);
        return null;
    }
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export async function generateMaintenanceSchedule(car: Car, language: string, existingTasks: CarTask[] = []): Promise<Partial<CarTask>[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Unauthorized');

    const response = await fetch(`${WORKER_URL}/ai/maintenance-schedule`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ car, language, existingTasks })
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Generation failed' }));
        throw new Error(err.error || 'Generation failed');
    }

    const taskArray = await response.json();
    
    if (!Array.isArray(taskArray)) {
        throw new Error('AI did not return an array of tasks.');
    }

    return taskArray.map((t: any) => ({
      ...t,
      ai_generated: true,
      last_performed_mileage: null,
      last_performed_date: null,
      car_id: car.id,
      user_id: car.user_id,
    }));
  } catch (error) {
    console.error('Error in AI maintenance generation:', error);
    return [];
  }
}
